// Pure, deterministic animal behavior. NO Phaser / config / DOM imports — every
// input arrives via arguments so this runs under `node --test`. Any client feeding
// identical inputs gets identical output, which is what gives visitor parity.

export const DAY_CYCLE_SECONDS = 240;
// [name, start, end) seconds within the cycle — must match backend PHASES / dayCycle.js.
export const PHASES = [
  ['night', 0, 60],
  ['morning', 60, 120],
  ['day', 120, 180],
  ['evening', 180, 240],
];

// Phase + 0..1 progress within it, derived from the shared clock anchor.
export function phaseInfo(nowMs, cycleStartedMs) {
  let s = (((nowMs - cycleStartedMs) / 1000) % DAY_CYCLE_SECONDS);
  if (s < 0) s += DAY_CYCLE_SECONDS;
  for (const [name, start, end] of PHASES) {
    if (s >= start && s < end) return { phase: name, progress: (s - start) / (end - start), s };
  }
  return { phase: 'night', progress: 0, s };
}

// FNV-1a 32-bit hash of the joined parts → uint32. Deterministic across engines.
export function hash(...parts) {
  const str = parts.join('|');
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}

// Deterministic float in [0,1).
export function rand01(...parts) {
  return (hash(...parts) % 1_000_000) / 1_000_000;
}

export const MOOD = { HUNGRY: 'hungry', HAPPY: 'happy', CONTENT: 'content', SAD: 'sad' };

export function moodOf(animal) {
  if (animal.is_hungry) return MOOD.HUNGRY;
  const h = animal.happiness ?? 0;
  if (h >= 66) return MOOD.HAPPY;
  if (h >= 33) return MOOD.CONTENT;
  return MOOD.SAD;
}

// radius/speed in px; bob is a 0..1 liveliness multiplier; social = eligible for rendezvous.
const MOOD_PARAMS = {
  [MOOD.HUNGRY]:  { radius: 60,  speed: 60,  bob: 0.4, social: false },
  [MOOD.HAPPY]:   { radius: 220, speed: 150, bob: 1.0, social: true },
  [MOOD.CONTENT]: { radius: 140, speed: 100, bob: 0.7, social: true },
  [MOOD.SAD]:     { radius: 70,  speed: 60,  bob: 0.3, social: false },
};

export function moodParams(mood) {
  return MOOD_PARAMS[mood] ?? MOOD_PARAMS[MOOD.CONTENT];
}

// How far below the buda the daytime roaming is centered (px). Kennels sit on a
// raised platform; animals should mill about in the open yard in front of them.
export const ROAM_DROP = 170;

// Waypoint wander: the animal walks to a deterministic point near its buda, rests,
// then walks to the next — which reads as walking, unlike a fast continuous drift
// (sinusoids oscillated ~200px/s and any follow looked like floating or shaking).
const WANDER_LEG = 3.5;                          // seconds to walk one leg
const WANDER_PAUSE = 1.6;                        // seconds resting at a waypoint
const WANDER_CYCLE = WANDER_LEG + WANDER_PAUSE;

// Deterministic offset (relative to home) of the k-th waypoint, within `radius`.
// Vertical is flattened so animals stay grounded near the buda.
function waypoint(id, k, radius) {
  const ang = rand01(id, 'wa', k) * Math.PI * 2;
  const r = (0.25 + 0.75 * rand01(id, 'wr', k)) * radius;
  // Vertical strongly flattened: animals roam in a low horizontal band in the yard
  // rather than swinging back up toward the kennel platform.
  return { ox: Math.cos(ang) * r, oy: Math.sin(ang) * r * 0.30 };
}

// Position offset at time tSec: smoothstep-walk from waypoint k to k+1 over WANDER_LEG,
// then hold at k+1 for WANDER_PAUSE. Continuous across cycles (each ends where the next begins).
function wanderOffset(id, tSec, radius) {
  const k = Math.floor(tSec / WANDER_CYCLE);
  const tInCycle = tSec - k * WANDER_CYCLE;
  const from = waypoint(id, k, radius);
  const to = waypoint(id, k + 1, radius);
  const p = tInCycle < WANDER_LEG ? tInCycle / WANDER_LEG : 1;   // 1 = resting at `to`
  const e = p * p * (3 - 2 * p);                                  // smoothstep ease-in/out
  return { ox: from.ox + (to.ox - from.ox) * e, oy: from.oy + (to.oy - from.oy) * e };
}

// blend factor toward home: 1 = at buda, 0 = full free wander.
function homeBias(phase, progress) {
  if (phase === 'morning') return 1 - progress;   // emerge: home -> roam
  if (phase === 'evening') return progress;        // retreat: roam -> home
  if (phase === 'day') return 0;
  return 1; // night handled by hidden; morning/day/evening only reach here otherwise
}

// Vertical extent of the wander band around its centre (waypoint oy is radius * 0.30).
const WANDER_Y_EXTENT = 0.30;

// Keep the whole wander band inside yBounds (band centre ± radius*0.30). No-op without bounds.
function clampRoamCenterY(cy, radius, yBounds) {
  if (!yBounds) return cy;
  const half = radius * WANDER_Y_EXTENT;
  return Math.max(yBounds[0] + half, Math.min(yBounds[1] - half, cy));
}

function clampY(y, yBounds) {
  return yBounds ? Math.max(yBounds[0], Math.min(yBounds[1], y)) : y;
}

// yBounds (optional [min,max]): the scene's visible vertical band for an animal's centre. The
// daytime roam band is shifted UP so it fits inside it — a buda placed on the lowest floor row
// (a cat bed at the bottom of a room) would otherwise centre the roaming ROAM_DROP px below the
// screen and the animal would walk around entirely out of view.
export function baseTarget(animal, t, cycleStartedMs, phaseOverride = null, yBounds = null) {
  const { phase, progress } = phaseOverride
    ? { phase: phaseOverride, progress: 0.5 }
    : phaseInfo(t, cycleStartedMs);
  const mood = moodOf(animal);
  const home = animal.buda;
  if (phase === 'night') return { hidden: true, x: home.x, y: home.y, mood };

  const { radius } = moodParams(mood);
  const tSec = t / 1000;
  const { ox, oy } = wanderOffset(animal.id, tSec, radius);
  const bias = homeBias(phase, progress);
  // Daytime roaming is centered ROAM_DROP below the buda (out in the yard); emerging
  // (morning) and retreating (evening) blend back up to the buda itself.
  const roamX = home.x + ox;
  const roamY = clampRoamCenterY(home.y + ROAM_DROP, radius, yBounds) + oy;
  const x = home.x + (roamX - home.x) * (1 - bias);
  const y = home.y + (roamY - home.y) * (1 - bias);
  return { hidden: false, x, y, mood };
}

// Prey -> the oblivious types it flees. Predators (pies, krowa) are absent: they never react.
export const FLEE_TABLE = { kura: ['pies', 'krowa', 'kot'], kot: ['pies'] };
const FLEE_RADIUS = 200;
const FLEE_PUSH = 160;
const RENDEZVOUS_PERIOD = 30;  // seconds per social "bucket"
const RENDEZVOUS_ACTIVE = 6;   // seconds of an actual meeting per bucket (rest is free roaming)

function badgeFor(animal, hidden, mood) {
  if (hidden) return 'sleep';
  if (animal.is_hungry) return 'hungry';
  if ((animal.pending_count ?? 0) > 0) return 'product';
  return null;
}

// Deterministic follow target: owner if present, else present player minimizing hash(playerId, animalId).
export function followTargetId(animal, players, ownerId) {
  if (!players.length) return null;
  if (ownerId && players.some((p) => p.id === ownerId)) return ownerId;
  let best = null;
  let bestKey = Infinity;
  for (const p of players) {
    const k = hash(p.id, animal.id);
    if (k < bestKey) { bestKey = k; best = p.id; }
  }
  return best;
}

// One scheduled social rendezvous per scene per time bucket: a SAME-TYPE pair meets at a
// point. Same-type only — animals never flee their own type, so this both honors the
// "same-type biased" rule and never pairs an animal with one it fears (cross-type animals
// just wander/flee instead). Returns { ids:Set, point:{x,y} } or null. Eligible = awake,
// non-hungry, social mood. ALL ordering is by animal id (never ctx.animals array order), so
// every client computes the same pair regardless of the order animals arrive in.
function rendezvous(ctx, eligible) {
  if (eligible.length < 2) return null;
  const tSec = (ctx.nowMs - ctx.cycleStartedMs) / 1000;
  const bucket = Math.floor(tSec / RENDEZVOUS_PERIOD);
  // Only a brief window of each bucket is an actual meeting; the rest is free roaming,
  // so a pair isn't pinned together (and away from the roam band) the whole time.
  if (tSec - bucket * RENDEZVOUS_PERIOD > RENDEZVOUS_ACTIVE) return null;
  const seed = hash(ctx.sceneId, bucket);
  const byType = {};
  for (const a of eligible) (byType[a.type] ??= []).push(a);
  for (const t of Object.keys(byType)) byType[t].sort((p, q) => (p.id < q.id ? -1 : 1));
  const pairTypes = Object.keys(byType).filter((t) => byType[t].length >= 2).sort();
  if (!pairTypes.length) return null;   // no same-type pair available this bucket
  const pool = byType[pairTypes[seed % pairTypes.length]];
  const i = seed % pool.length;
  const j = (i + 1 + (hash(seed, 'j') % (pool.length - 1))) % pool.length;
  const a = pool[i];
  const b = pool[j];
  if (a.id === b.id) return null;
  // Meet down in the roam band (ROAM_DROP below the budy midpoint), nudged deterministically.
  // The pair stands side by side (±SEP) rather than on the exact same spot (which looked
  // like a single animal). a/b are id-ordered, so the offsets are input-order-independent.
  const cx = (a.buda.x + b.buda.x) / 2 + (rand01(seed, 'px') - 0.5) * 120;
  const cy = clampY((a.buda.y + b.buda.y) / 2 + ROAM_DROP + (rand01(seed, 'py') - 0.5) * 40, ctx.yBounds);
  const SEP = 38;
  const points = new Map([
    [a.id, { x: cx - SEP, y: cy }],
    [b.id, { x: cx + SEP, y: cy }],
  ]);
  return { points };
}

export function computeScene(ctx) {
  const out = new Map();
  // 1. Base targets for everyone (predators included — they are oblivious).
  const base = new Map();
  for (const a of ctx.animals) base.set(a.id, baseTarget(a, ctx.nowMs, ctx.cycleStartedMs, ctx.phaseOverride, ctx.yBounds));

  // 2. Eligible-for-social = awake, non-hungry, social mood.
  const eligible = ctx.animals.filter((a) => {
    const b = base.get(a.id);
    return !b.hidden && !a.is_hungry && moodParams(b.mood).social;
  });
  const meet = rendezvous(ctx, eligible);

  for (const a of ctx.animals) {
    const b = base.get(a.id);
    let { x, y } = b;
    let interacting = false;

    if (!b.hidden) {
      // 3. Hungry follow overrides wander.
      if (a.is_hungry) {
        const tid = followTargetId(a, ctx.players, ctx.ownerId);
        const target = tid ? ctx.players.find((p) => p.id === tid) : null;
        if (target) {
          // ease toward the player but stop a little short (begging distance).
          const dx = target.x - a.buda.x;
          const dy = target.y - a.buda.y;
          const d = Math.hypot(dx, dy) || 1;
          const stop = Math.max(0, d - 70);   // stop 70px short; never overshoot past the player
          x = a.buda.x + dx / d * stop;
          y = a.buda.y + dy / d * stop;
          interacting = true;
        }
      } else if (meet && meet.points.has(a.id)) {
        // 4. Social rendezvous overrides wander — each of the pair stands at its own spot.
        const p = meet.points.get(a.id);
        x = p.x;
        y = p.y;
        interacting = true;
      } else {
        // 5. Flee overlay: push away from each nearby oblivious predator's base position.
        const scared = FLEE_TABLE[a.type] ?? [];
        for (const other of ctx.animals) {
          if (other.id === a.id || !scared.includes(other.type)) continue;
          const ob = base.get(other.id);
          if (ob.hidden) continue;
          const dx = x - ob.x;
          const dy = y - ob.y;
          const dist = Math.hypot(dx, dy) || 1;
          if (dist < FLEE_RADIUS) {
            const push = (FLEE_RADIUS - dist) / FLEE_RADIUS * FLEE_PUSH;
            x += dx / dist * push;
            y += dy / dist * push;
          }
        }
      }
      // Clamp to the scene's walkable bounds (X always; Y when the scene provides yBounds —
      // covers hungry-follow and flee pushes, which can aim past the screen edge).
      x = Math.max(ctx.xBounds[0], Math.min(ctx.xBounds[1], x));
      y = clampY(y, ctx.yBounds);
    }

    out.set(a.id, {
      hidden: b.hidden, x, y, mood: b.mood,
      badge: badgeFor(a, b.hidden, b.mood), interacting,
    });
  }
  return out;
}
